import { Link } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/useAuth';
import { useLanguage } from '../i18n';
import { fetchAdminUsers, updateAdminUser, deleteAdminUser, type AdminUser } from '../services/admin';
import { Shield, Loader2, AlertCircle, Trash2, RefreshCw, Users, ArrowLeft } from 'lucide-react';

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const { t } = useLanguage();
  const a = t.admin;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchAdminUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : a.errorLoad);
    } finally {
      setLoading(false);
    }
  }, [a.errorLoad]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleUpdate = async (target: AdminUser, updates: Partial<AdminUser>) => {
    setSavingId(target.id);
    setError('');
    try {
      const updated = await updateAdminUser({
        id: target.id,
        plan: updates.plan,
        role: updates.role,
        generations_used: updates.generations_used,
        generations_limit: updates.generations_limit,
      });
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : a.errorSave);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (target: AdminUser) => {
    if (!confirm(a.deleteConfirm(target.email))) return;
    setDeletingId(target.id);
    setError('');
    try {
      await deleteAdminUser(target.id);
      setUsers((prev) => prev.filter((u) => u.id !== target.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : a.errorDelete);
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetUsage = async (target: AdminUser) => {
    await handleUpdate(target, { generations_used: 0 });
  };

  if (!isAdmin) {
    return (
      <div className="h-full overflow-auto p-8">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-100 p-8 text-center">
          <AlertCircle size={32} className="text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-red-900 mb-2">{a.accessDenied}</h1>
          <p className="text-red-800">{a.adminOnly}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/app"
              className="p-2 text-slate hover:text-ink hover:bg-mist transition-colors"
              title="Back to app"
            >
              <ArrowLeft size={20} />
            </Link>
            <div className="w-12 h-12 bg-red-50 border border-red-100 flex items-center justify-center">
              <Shield size={24} className="text-red-500" />
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-ink">{a.title}</h1>
              <p className="text-slate">{a.subtitle}</p>
            </div>
          </div>
          <button
            onClick={loadUsers}
            disabled={loading}
            className="px-4 py-2 border border-cloud hover:bg-mist text-ink font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {a.refresh}
          </button>
        </div>

        {user && (
          <div className="bg-white border border-cloud p-4 mb-6 flex items-center gap-4">
            <Users size={20} className="text-slate" />
            <div>
              <p className="text-sm text-slate">{a.signedInAs}</p>
              <p className="font-semibold text-ink">{user.email}</p>
            </div>
            <span className="ml-auto px-3 py-1 text-xs font-semibold bg-red-500 text-white">{t.plans[user.plan]}</span>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 flex items-start gap-3 text-red-800">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-slate">
            <Loader2 size={48} className="mx-auto mb-4 text-red-400 animate-spin" />
            <p>{a.loading}</p>
          </div>
        ) : users.length === 0 ? (
          <div className="bg-white border border-cloud p-12 text-center text-slate">{a.empty}</div>
        ) : (
          <div className="bg-white border border-cloud overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-mist border-b border-cloud">
                  <tr>
                    <th className="px-4 py-3 font-semibold text-ink">{a.email}</th>
                    <th className="px-4 py-3 font-semibold text-ink">{a.plan}</th>
                    <th className="px-4 py-3 font-semibold text-ink">{a.role}</th>
                    <th className="px-4 py-3 font-semibold text-ink">{a.usage}</th>
                    <th className="px-4 py-3 font-semibold text-ink">{a.created}</th>
                    <th className="px-4 py-3 font-semibold text-ink text-right">{a.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-mist last:border-b-0">
                      <td className="px-4 py-3">
                        <p className="font-medium text-ink">{u.email}</p>
                        <p className="text-xs text-ash font-mono">{u.id.slice(0, 8)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.plan}
                          onChange={(e) => handleUpdate(u, { plan: e.target.value as AdminUser['plan'] })}
                          disabled={savingId === u.id}
                          className="px-2 py-1 border border-cloud bg-paper text-ink focus:outline-none focus:border-red-500"
                        >
                          <option value="starter">{t.plans.starter}</option>
                          <option value="founder">{t.plans.founder}</option>
                          <option value="scale">{t.plans.scale}</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          onChange={(e) => handleUpdate(u, { role: e.target.value as AdminUser['role'] })}
                          disabled={savingId === u.id || u.id === user?.id}
                          className="px-2 py-1 border border-cloud bg-paper text-ink focus:outline-none focus:border-red-500"
                        >
                          <option value="user">{a.userRole}</option>
                          <option value="admin">{a.adminRole}</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-ink font-medium">
                            {u.generations_used.toLocaleString()} / {u.generations_limit.toLocaleString()}
                          </span>
                          <button
                            onClick={() => handleResetUsage(u)}
                            disabled={savingId === u.id}
                            className="p-1.5 text-slate hover:text-red-500 hover:bg-red-50 border border-cloud transition-colors"
                            title={a.resetUsage}
                          >
                            <RefreshCw size={14} />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ash">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleDelete(u)}
                          disabled={deletingId === u.id || u.id === user?.id}
                          className="p-2 text-ash hover:text-red-500 hover:bg-red-50 border border-cloud transition-colors disabled:opacity-50"
                          title={a.delete}
                        >
                          {deletingId === u.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
